import { useEffect, useRef } from 'react';
import { logout, exitImpersonationLocally } from '../api/queryHooks/user';
import { getTokenTimeRemaining, readImpersonation } from '../utils/jwt';
import notify from '../utils/notify';

const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const getCheckInterval = (): number => {
  const envInterval = import.meta.env.VITE_TOKEN_CHECK_INTERVAL_MINUTES;
  if (envInterval) {
    const minutes = parseInt(envInterval, 10);
    if (!Number.isNaN(minutes) && minutes > 0) {
      return minutes * 60 * 1000;
    }
  }
  return DEFAULT_CHECK_INTERVAL_MS;
};

/**
 * Periodically check JWT token validity and force a logout near expiration.
 * Default check interval is 5 minutes; configurable via VITE_TOKEN_CHECK_INTERVAL_MINUTES.
 */
export const useTokenExpirationMonitor = (): void => {
  const warningShownRef = useRef(false);
  const checkIntervalMs = useRef(getCheckInterval());

  useEffect(() => {
    const TOKEN_KEY = 'token';

    const checkTokenExpiration = () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        warningShownRef.current = false;
        return;
      }

      const timeRemainingSeconds = getTokenTimeRemaining(token);
      const checkIntervalSeconds = checkIntervalMs.current / 1000;

      if (timeRemainingSeconds < checkIntervalSeconds) {
        // A 30-minute impersonation token hits this routinely. Dropping the
        // admin out of the app entirely would be wrong — end the impersonation
        // and hand them back their own session instead.
        if (readImpersonation(token)) {
          exitImpersonationLocally();
          notify('notify.warning', 'notify.impersonation.ended', { type: 'warning' });
        } else {
          logout();
          notify('notify.warning', 'notify.session.expired', { type: 'warning' });
        }
        warningShownRef.current = false;
        return;
      }

      if (
        timeRemainingSeconds < checkIntervalSeconds * 2 &&
        !warningShownRef.current
      ) {
        notify('notify.warning', 'notify.session.expiring_soon', {
          type: 'warning',
          dismiss: { duration: 10000, onScreen: false, pauseOnHover: true, waitForAnimation: false },
          insert: 'bottom',
        });
        warningShownRef.current = true;
        return;
      }

      if (timeRemainingSeconds >= checkIntervalSeconds * 2) {
        warningShownRef.current = false;
      }
    };

    checkTokenExpiration();

    const intervalId = setInterval(checkTokenExpiration, checkIntervalMs.current);

    return () => {
      clearInterval(intervalId);
    };
  }, []);
};

export default useTokenExpirationMonitor;
